import * as RG from '@radix-ui/react-radio-group';
import { SOURCES_LIST } from 'modules/dataWarehouse/constants';
import { useDataQueryContext } from 'modules/dataWarehouse/contexts';
import { ModalAction, SourceDetail } from 'modules/dataWarehouse/types';
import { Fragment, SyntheticEvent, useState } from 'react';
import styled from 'styled-components';
import { BUTTON_VARIANTS, Button } from 'ui/button';
import { MODAL_SIZES, Modal, ModalBody, ModalFooter, ModalHeader } from 'ui/modal';
import { Body1, colors, duration, easing, largeShadow, smallShadow, spacing } from 'ui/theme';
import { useModalContext } from 'utils/contexts/modal';
import { pxToRem } from 'utils/convert';

const RadioGroup = styled(RG.Root)`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(${pxToRem(220)}, 0.5fr));
  gap: ${spacing.plusHalf};
`;

const RadioItem = styled(RG.Item)`
  display: flex;
  align-items: center;
  gap: ${spacing.base};
  min-height: 100%;
  width: 100%;
  padding: ${spacing.plusQuarter} ${spacing.base};
  background-color: ${colors.white};
  border: ${spacing.single} solid ${colors.borderPrimary};
  border-radius: ${spacing.quarter};
  ${smallShadow}
  transition: all ${duration.medium} ${easing.standard};

  &[disabled] {
    cursor: not-allowed;
    background-color: ${colors.backgroundDisabled};
    border-color: ${colors.borderStrong};
  }

  &:not([disabled]) {
    &:hover {
      border-color: ${colors.borderEmphasis};
      ${largeShadow}
    }
  }

  &[aria-checked='true'] {
    border-color: ${colors.purple50};
    outline: ${spacing.eighth} solid ${colors.purple50};

    &:hover {
      border-color: ${colors.purple50};
    }
  }

  &:focus-visible {
    outline-color: ${colors.linkFocusOutline};
  }
`;

const StyledName = styled(Body1)`
  text-align: initial;

  [disabled] & {
    color: ${colors.textDisabled};
  }
`;

const StyledIcon = styled.svg`
  flex: 0 0 auto;

  [disabled] & {
    opacity: 0.65;
  }
`;

export const CATALOG_MODAL_TEXT = {
  buttonId: 'connect-source-button',
  buttonLabel: 'Continue',
  modalHeader: 'Select a source',
  modalId: 'source-catalog',
  radioButtonId: 'source-item',
  radioGroupId: 'source-group',
  radioTooltipContent: 'Coming soon',
};

export const CatalogModal = ({ onClose }: { onClose: () => void }) => {
  const { allowedSourceTypes } = useDataQueryContext();
  const { showModal } = useModalContext();
  const [selected, setSelected] = useState('');

  const sourceList = allowedSourceTypes.reduce((acc, sourceType = '') => {
    const currentSource = SOURCES_LIST[sourceType];
    if (!currentSource || currentSource.isHidden) return acc;
    acc.push(currentSource);
    return acc;
  }, [] as SourceDetail[]);

  const sortedSources = sourceList.sort((a, b) => a.name.localeCompare(b.name));
  const modalSize = sourceList.length > 6 ? MODAL_SIZES.LARGE : MODAL_SIZES.MEDIUM;

  const handleSubmit = (e: SyntheticEvent) => {
    e.preventDefault();
    showModal({ action: ModalAction.CREATE_SOURCE, payload: { sourceType: selected } });
  };

  return (
    <Modal id={CATALOG_MODAL_TEXT.modalId} isModalOpen onClose={onClose} size={modalSize}>
      <ModalHeader>{CATALOG_MODAL_TEXT.modalHeader}</ModalHeader>
      <ModalBody>
        <RadioGroup
          data-test={CATALOG_MODAL_TEXT.radioGroupId}
          name={CATALOG_MODAL_TEXT.radioGroupId}
          onValueChange={setSelected}
          value={selected}
        >
          {sortedSources.map((source) => {
            const { Icon, id, name } = source;
            const isDisabled = !allowedSourceTypes.includes(source.id);
            return (
              <Fragment key={id}>
                <RadioItem
                  data-test={`${id}-${CATALOG_MODAL_TEXT.radioButtonId}`}
                  disabled={isDisabled}
                  value={id}
                >
                  <StyledIcon as={Icon} width={40} height={40} />
                  <StyledName as="span">{name}</StyledName>
                </RadioItem>
              </Fragment>
            );
          })}
        </RadioGroup>
      </ModalBody>
      <ModalFooter hasCancel>
        <Button
          data-test={CATALOG_MODAL_TEXT.buttonId}
          isDisabled={!selected}
          onClick={handleSubmit}
          variant={BUTTON_VARIANTS.PRIMARY}
        >
          {CATALOG_MODAL_TEXT.buttonLabel}
        </Button>
      </ModalFooter>
    </Modal>
  );
};
